/**
 * Footer - Site footer with navigation and newsletter signup
 * Displays links, event details, and social media
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../ComponentsStyling/Footer.css';

/* Navigation configuration */

const COLUMNS = [
    {
        title: 'Event',
        index: '01',
        links: [
            { label: 'About', to: '/about' },
            { label: 'Competition', to: '/program' },
            { label: 'Workshops', to: '/workshops' },
            { label: 'Expo Day', to: '/expo-day' },
            { label: 'Schedule', to: '/agenda' },
            { label: 'Speakers', to: '/speakers' },
            { label: 'Judges', to: '/judges' },
        ],
    },
    {
        title: 'Participate',
        index: '02',
        links: [
            { label: 'Register your project', to: '/program#track-selector' },
            { label: 'Explore Expo Day', to: '/expo-day' },
            { label: 'Workshop Registration', to: '/workshops' },
        ],
    },
    {
        title: 'Info',
        index: '03',
        links: [
            { label: 'Sponsors & Partners', to: '/partners' },
            { label: 'FAQ', to: '/faq' },

        ],
    },
];

/* Social media links */

const SOCIAL = [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/ai-expo2026/?viewAsMember=true', icon: 'in' },
    { label: 'Instagram', href: 'https://www.instagram.com/aiexpo2026?igsh=NTllbmJ5YXZ6dGVm', icon: 'ig' },
];

/* Social icon components */

const SocialIcon = ({ name }) => {
    switch (name) {
        case 'in':
            return (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect x="2" y="2" width="20" height="20" rx="2"
                        stroke="currentColor" strokeWidth="1.4" />
                    <path d="M7 10v7M7 7v0M11 17v-4a3 3 0 016 0v4M11 10v7"
                        stroke="currentColor" strokeWidth="1.4"
                        strokeLinecap="round" />
                </svg>
            );
        case 'ig':
            return (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect x="3" y="3" width="18" height="18" rx="4"
                        stroke="currentColor" strokeWidth="1.4" />
                    <circle cx="12" cy="12" r="4"
                        stroke="currentColor" strokeWidth="1.4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
                </svg>
            );
        default:
            return null;
    }
};

/* Footer component */

const Footer = () => {
    const [year, setYear] = useState('2026');
    const [sub, setSub] = useState(null);

    useEffect(() => {
        /* Set copyright year to current year, minimum 2026 */
        const y = Math.max(2026, new Date().getFullYear());
        setYear(String(y));
    }, []);

    const scrollToTop = () => {
        /* Smooth scroll to top */
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /* Notify-me signup — posts to web3forms (same endpoint/key as the Contact
       form). The team receives each interested email and follows up when dates
       land. Real, honest capture — no fake "subscribed" state. */
    const handleSubscribe = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        setSub({ type: 'loading', msg: '' });
        try {
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                body: new FormData(form),
                headers: { Accept: 'application/json' },
            });
            if (res.ok) {
                setSub({ type: 'success', msg: "You're on the list — we'll be in touch." });
                form.reset();
                setTimeout(() => setSub(null), 6000);
            } else {
                setSub({ type: 'error', msg: 'Something went wrong. Please try again.' });
            }
        } catch (err) {
            setSub({ type: 'error', msg: 'Network error. Please try again.' });
        }
    };

    return (
        <footer className="ftr" id="footer">

            {/* Decorative background */}
            <div className="ftr__grid" aria-hidden />
            <div className="ftr__noise" aria-hidden />

            <div className="ftr__wrap">

                {/* ───────── 01 · END RAIL (HUD-style) ───────── */}
                <div className="ftr__endrail">
                    <span className="ftr__endrail-tag">
                        <span className="ftr__endrail-led" />
                        END&nbsp;//&nbsp;OF&nbsp;//&nbsp;TRANSMISSION
                    </span>
                    <span className="ftr__endrail-line" />
                    <span className="ftr__endrail-mid">
                        SIGNAL&nbsp;COMPLETE
                    </span>
                    <span className="ftr__endrail-line" />
                    <button
                        type="button"
                        className="ftr__totop"
                        onClick={scrollToTop}
                        aria-label="Back to top"
                    >
                        <span className="ftr__totop-bracket">[</span>
                        <span className="ftr__totop-text">BACK&nbsp;TO&nbsp;TOP</span>
                        <span className="ftr__totop-arrow">
                            <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M8 14V2m0 0L3 7m5-5l5 5"
                                    stroke="currentColor" strokeWidth="1.4"
                                    strokeLinecap="square" />
                            </svg>
                        </span>
                        <span className="ftr__totop-bracket">]</span>
                    </button>
                </div>

                {/* Navigation and newsletter sections */}
                <div className="ftr__upper">

                    <nav className="ftr__nav" aria-label="Footer navigation">
                        {COLUMNS.map((col) => (
                            <div className="ftr__col" key={col.title}>
                                <div className="ftr__col-head">
                                    <span className="ftr__col-index">DIR/{col.index}</span>
                                    <span className="ftr__col-rule" />
                                </div>
                                <h3 className="ftr__col-title">
                                    {col.title}
                                </h3>
                                <ul className="ftr__col-list">
                                    {col.links.map((link, i) => (
                                        <li key={link.label} style={{ '--j': i }}>
                                            <Link to={link.to} className="ftr__col-link">
                                                <span className="ftr__col-link-bullet" aria-hidden />
                                                <span className="ftr__col-link-label">
                                                    {link.label}
                                                </span>
                                                <span className="ftr__col-link-arrow" aria-hidden>
                                                    <svg viewBox="0 0 12 12" fill="none">
                                                        <path d="M3 9L9 3M9 3H4M9 3v5"
                                                            stroke="currentColor"
                                                            strokeWidth="1.2"
                                                            strokeLinecap="square" />
                                                    </svg>
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className="ftr__newsletter">
                        <div className="ftr__col-head">
                            <span className="ftr__col-index">SUB/04</span>
                            <span className="ftr__col-rule" />
                        </div>
                        <h3 className="ftr__col-title">
                            Stay Updated
                        </h3>
                        <p className="ftr__newsletter-text">
                            Get notified when registration opens, workshops go live, and
                            the schedule is announced. No spam — just the dates that matter.
                        </p>
                        <form className="ftr__news-form" onSubmit={handleSubscribe}>
                            <input type="hidden" name="access_key" value="522512a8-f5d9-4bfb-a15c-b1679b3b6b74" />
                            <input type="hidden" name="subject" value="AI Expo Jordan 2026 — Notify me signup" />
                            <input type="hidden" name="from_name" value="AI Expo Jordan — Footer" />
                            <div className="ftr__news-row">
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    className="ftr__news-input"
                                    placeholder="your.email@example.com"
                                    aria-label="Email address for updates"
                                    disabled={sub?.type === 'loading'}
                                />
                                <button
                                    type="submit"
                                    className="ftr__news-btn"
                                    disabled={sub?.type === 'loading'}
                                    aria-label="Notify me"
                                >
                                    {sub?.type === 'loading' ? (
                                        <span className="ftr__news-btn-dots" aria-hidden>···</span>
                                    ) : (
                                        <svg viewBox="0 0 24 12" fill="none" aria-hidden>
                                            <path d="M1 6h22m0 0L18 1m5 5l-5 5"
                                                stroke="currentColor" strokeWidth="1.4"
                                                strokeLinecap="square" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="ftr__form-status" aria-live="polite">
                                {sub && sub.msg && (
                                    <span className={`ftr__form-status-msg ftr__form-status-msg--${sub.type}`}>
                                        {sub.msg}
                                    </span>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {/* ───────── 04 · WEBMASTER CREDIT ───────── */}
                <div className="ftr__credit">
                    <span className="ftr__credit-label">DESIGNED&nbsp;&amp;&nbsp;DEVELOPED&nbsp;BY</span>
                    <a
                        href="https://khaledraed.com/"
                        className="ftr__credit-name"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Khaled&nbsp;Raed
                    </a>
                    <span className="ftr__credit-divider" aria-hidden />
                    <div className="ftr__credit-links">
                        <a
                            href="https://khaledraed.com/"
                            className="ftr__credit-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Portfolio
                            <svg viewBox="0 0 12 12" fill="none" aria-hidden>
                                <path d="M3 9L9 3M9 3H4M9 3v5"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="square" />
                            </svg>
                        </a>
                        <a
                            href="https://www.linkedin.com/in/khaled-raed-0a92412a7/"
                            className="ftr__credit-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            LinkedIn
                            <svg viewBox="0 0 12 12" fill="none" aria-hidden>
                                <path d="M3 9L9 3M9 3H4M9 3v5"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="square" />
                            </svg>
                        </a>
                    </div>
                </div>

                {/* ───────── 05 · WORDMARK ───────── */}
                <div className="ftr__wordmark" aria-hidden>
                    <span className="ftr__wordmark-line">
                        <span className="ftr__wordmark-text">AI&nbsp;EXPO</span>
                    </span>
                    <span className="ftr__wordmark-line">
                        <span className="ftr__wordmark-text ftr__wordmark-text--outline">
                            JORDAN
                        </span>
                        <span className="ftr__wordmark-text">2026</span>
                    </span>
                </div>

                {/* ───────── 05 · META STRIP ───────── */}
                <div className="ftr__meta">
                    <div className="ftr__meta-cell">
                        <span className="ftr__meta-label">DATE</span>
                        <span className="ftr__meta-value">1 &amp; 3 OCT 2026</span>
                    </div>
                    <span className="ftr__meta-divider" aria-hidden />
                    <div className="ftr__meta-cell">
                        <span className="ftr__meta-label">VENUE</span>
                        <span className="ftr__meta-value">UJ ACADEMY</span>
                    </div>
                    <span className="ftr__meta-divider" aria-hidden />
                    <div className="ftr__meta-cell">
                        <span className="ftr__meta-label">CITY</span>
                        <span className="ftr__meta-value">TO BE ANNOUNCED</span>
                    </div>
                    <span className="ftr__meta-divider" aria-hidden />
                    <div className="ftr__meta-cell">
                        <span className="ftr__meta-label">ORGANIZER</span>
                        <span className="ftr__meta-value"><b>IEEE CIS UJ</b></span>
                    </div>
                </div>

                {/* Copyright and social links */}
                <div className="ftr__utility">
                    <div className="ftr__copyright">
                        <span className="ftr__copyright-mark">©</span>
                        <span>{year}&nbsp;AI&nbsp;Expo&nbsp;Jordan</span>
                        <span className="ftr__copyright-sep">{"//"}</span>
                        <span>All&nbsp;rights&nbsp;reserved</span>
                    </div>

                    <div className="ftr__social">
                        <span className="ftr__social-label">{"FOLLOW\u00a0//"}</span>
                        {SOCIAL.map((s) => (
                            <a
                                key={s.label}
                                href={s.href}
                                className="ftr__social-link"
                                aria-label={s.label}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className="ftr__social-corner ftr__social-corner--tl" />
                                <span className="ftr__social-corner ftr__social-corner--tr" />
                                <span className="ftr__social-corner ftr__social-corner--bl" />
                                <span className="ftr__social-corner ftr__social-corner--br" />
                                <SocialIcon name={s.icon} />
                            </a>
                        ))}
                    </div>
                </div>

            </div>
        </footer>
    );
};

export default Footer;